/*
 * Card gallery dots + slideshow reset.
 *
 * This used to be an inline <script> inside snippets/card-gallery.liquid, which meant the
 * whole body was emitted once per product card - 22 copies (~117 KB) on the homepage, more
 * on collection pages - and every copy ran a settle pass while the HTML was still parsing.
 * A pass reads scroller.clientWidth/scrollWidth and writes scrollLeft, so each one forces
 * layout; the homepage's galleries measured ~48 ms per pass, repeated at raf/120 ms/700 ms.
 *
 * Same public API and same end state, loaded once. The settle schedule is now per gallery:
 * a gallery settles when it comes within 300px of the viewport, so cards the visitor can
 * actually see behave exactly as before. Anything IntersectionObserver has not reached by
 * the load event is settled during idle time in small batches - IntersectionObserver is
 * inert in some embedded/hidden webviews, and the reset must not silently stop happening
 * there.
 *
 * Only galleries with two or more slides are tracked. A single-image gallery has nothing
 * to reset and no dots, yet settling it still forced a style and layout pass - on the SALE
 * page, where all 183 cards are single-image, that was ~400 forced passes and ~3 s of main
 * thread on a mid-range phone for no visible effect. New cards (quick add, pagination,
 * deferred carousel slides) are found from the mutation records themselves instead of
 * re-querying the whole document on every DOM change.
 */
(function () {
  var raf =
    typeof window.requestAnimationFrame === 'function'
      ? window.requestAnimationFrame.bind(window)
      : function (callback) {
          return window.setTimeout(callback, 16);
        };

  var idle =
    typeof window.requestIdleCallback === 'function'
      ? window.requestIdleCallback.bind(window)
      : function (callback) {
          return window.setTimeout(callback, 1);
        };

  var dots = window.JnorssCardDots;
  if (!dots) {
    dots = {
      setActive: function (target, index) {
        var wrap = target.querySelector('.card-gallery__dots');
        if (!wrap) return;

        var items = wrap.children;
        if (!items.length) return;

        if (typeof index !== 'number' || isNaN(index)) index = 0;
        if (index < 0) index = 0;
        if (index > items.length - 1) index = items.length - 1;

        for (var i = 0; i < items.length; i += 1) {
          if (i === index) items[i].classList.add('is-active');
          else items[i].classList.remove('is-active');
        }
      },
      sync: function (target) {
        if (!target || !target.isConnected) return;
        if (!target.querySelector('.card-gallery__dots')) return;

        var slideshow = target.querySelector('slideshow-component[ref="slideshow"]');
        if (!slideshow) return;

        var index = typeof slideshow.current === 'number' ? slideshow.current : 0;
        dots.setActive(target, index);
      },
      init: function (target) {
        if (!target.querySelector('.card-gallery__dots')) return;

        var slideshow = target.querySelector('slideshow-component[ref="slideshow"]');
        if (!slideshow) return;

        if (slideshow.dataset.jnorssDots === 'true') {
          dots.sync(target);
          return;
        }

        slideshow.dataset.jnorssDots = 'true';

        slideshow.addEventListener('slideshow:select', function (event) {
          var index = event && event.detail ? event.detail.index : null;
          if (typeof index !== 'number') {
            dots.sync(target);
            return;
          }
          dots.setActive(target, index);
        });

        var scroller = slideshow.querySelector('slideshow-slides[ref="scroller"]');
        if (scroller) {
          var frame = null;
          scroller.addEventListener(
            'scroll',
            function () {
              if (frame !== null) return;
              frame = raf(function () {
                frame = null;

                var width = scroller.clientWidth;
                if (!width || scroller.scrollWidth <= width + 1) return;

                dots.setActive(target, Math.round(Math.abs(scroller.scrollLeft) / width));
              });
            },
            { passive: true }
          );
        }

        dots.sync(target);
      }
    };
    window.JnorssCardDots = dots;
  }

  function slidesOf(target) {
    var slideshow = target.querySelector('slideshow-component[ref="slideshow"]');
    var scroller = slideshow && slideshow.querySelector('slideshow-slides[ref="scroller"]');
    var slides = [];
    var children = scroller ? scroller.children : [];

    for (var index = 0; index < children.length; index += 1) {
      if (children[index].tagName && children[index].tagName.toLowerCase() === 'slideshow-slide') {
        slides.push(children[index]);
      }
    }

    return { slideshow: slideshow, scroller: scroller, slides: slides };
  }

  var registry = window.JnorssCardGalleryFix;
  if (!registry) {
    registry = {
      galleries: [],
      installed: false,
      reset: function (target) {
        if (!target || !target.isConnected) return;
        if (!target.closest('product-card')) return;

        var parts = slidesOf(target);
        var slideshow = parts.slideshow;
        var scroller = parts.scroller;
        var slides = parts.slides;

        if (!slideshow || !scroller || !slides.length) return;

        slideshow.setAttribute('initial-slide', '0');
        if ('current' in slideshow) slideshow.current = 0;

        slides.forEach(function (slide, index) {
          slide.removeAttribute('reveal');
          slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
          if (index === 0) slide.hidden = false;
        });

        try {
          scroller.scrollTo({ left: 0, top: 0, behavior: 'auto' });
        } catch (error) {
          scroller.scrollLeft = 0;
          scroller.scrollTop = 0;
        }

        if (window.JnorssCardDots) window.JnorssCardDots.setActive(target, 0);
      },
      resetAll: function () {
        for (var index = this.galleries.length - 1; index >= 0; index -= 1) {
          var target = this.galleries[index];
          if (target.isConnected) this.reset(target);
          else this.galleries.splice(index, 1);
        }
      }
    };
    window.JnorssCardGalleryFix = registry;
  }

  /* --- discovery, viewport gating, idle fallback -------------------------- */

  function settle(gallery) {
    registry.reset(gallery);
    dots.init(gallery);
  }

  function activate(gallery, full) {
    if (gallery.dataset.jnorssGallery === 'active') return;
    gallery.dataset.jnorssGallery = 'active';

    settle(gallery);
    raf(function () {
      settle(gallery);
    });

    // slideshow-component hydrates after its markup is parsed, so a gallery reached during
    // page load needs the later passes the inline version used to do. Galleries swept in
    // idle time are already past that window and do not.
    if (!full) return;
    setTimeout(function () {
      settle(gallery);
    }, 120);
    setTimeout(function () {
      settle(gallery);
    }, 700);
  }

  var observer = null;
  if (typeof window.IntersectionObserver === 'function') {
    observer = new window.IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i += 1) {
          if (!entries[i].isIntersecting) continue;
          observer.unobserve(entries[i].target);
          activate(entries[i].target, true);
        }
      },
      // Settle before the card can be seen, so the reset is never visible to the user.
      { rootMargin: '300px 0px' }
    );
  }

  // Returns true when the gallery was newly registered.
  function register(gallery) {
    if (gallery.__jnorssTracked) return false;
    if (!gallery.closest('product-card')) return false;
    if (slidesOf(gallery).slides.length < 2) return false;

    gallery.__jnorssTracked = true;
    registry.galleries.push(gallery);

    if (gallery.dataset.jnorssGallery === 'active') return true;
    if (observer) observer.observe(gallery);
    else activate(gallery, true);
    return true;
  }

  function scan(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var added = false;

    if (scope.matches && scope.matches('.card-gallery') && register(scope)) added = true;

    var found = scope.querySelectorAll('.card-gallery');
    for (var i = 0; i < found.length; i += 1) {
      if (register(found[i])) added = true;
    }
    return added;
  }

  // Anything the observer has not reached by load is settled here, a few at a time, so the
  // page is never left depending on IntersectionObserver actually having fired.
  function sweep() {
    var handled = 0;

    for (var i = 0; i < registry.galleries.length && handled < 4; i += 1) {
      var gallery = registry.galleries[i];
      if (!gallery.isConnected) continue;
      if (gallery.dataset.jnorssGallery === 'active') continue;

      if (observer) observer.unobserve(gallery);
      activate(gallery, false);
      handled += 1;
    }

    if (handled) idle(sweep);
  }

  var loaded = document.readyState === 'complete';

  function startSweep() {
    loaded = true;
    idle(sweep);
  }

  scan(document);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      scan(document);
    });
  }

  if (loaded) startSweep();
  else window.addEventListener('load', startSweep);

  if (!registry.installed) {
    registry.installed = true;

    window.addEventListener('pageshow', function () {
      registry.resetAll();
    });

    document.addEventListener('shopify:section:load', function (event) {
      scan(event.target || document);
      registry.resetAll();
    });

    // Quick add, pagination, section rendering and deferred carousel slides inject cards
    // after load. Only the inserted subtrees are scanned.
    if (typeof window.MutationObserver === 'function') {
      var queue = [];
      var pending = false;

      var flush = function () {
        pending = false;
        var nodes = queue;
        queue = [];
        var added = false;

        for (var i = 0; i < nodes.length; i += 1) {
          var node = nodes[i];
          if (!node.isConnected) continue;
          if (scan(node)) added = true;

          // Slides added to an existing gallery can turn it into a multi-slide one.
          var host = node.parentElement && node.parentElement.closest('.card-gallery');
          if (host && register(host)) added = true;
        }

        if (added && loaded) idle(sweep);
      };

      new window.MutationObserver(function (records) {
        for (var r = 0; r < records.length; r += 1) {
          var addedNodes = records[r].addedNodes;
          for (var n = 0; n < addedNodes.length; n += 1) {
            var node = addedNodes[n];
            if (node.nodeType !== 1) continue;
            // A card gallery is always inside a product card; anything that cannot
            // hold one (text-level inline elements, scripts, styles) is skipped cheaply.
            if (node.firstElementChild || (node.matches && node.matches('.card-gallery'))) queue.push(node);
          }
        }

        if (queue.length && !pending) {
          pending = true;
          raf(flush);
        }
      }).observe(document.documentElement, { childList: true, subtree: true });
    }
  }
})();
